import React from "react";
import { Alert, Table } from "react-bootstrap";
import {
  SUMMARY_EMPTY_STATE,
  SUMMARY_KPI_COLUMN_HEADER,
  SUMMARY_TOTAL_HEADER,
  SUMMARY_UNSAVED_BANNER,
} from "@features/report/activity-entry/constants/activityEntrySummary";
import type { ActivityEntryFormGroup } from "@features/report/activity-entry/types/activityEntry";
import { isNumericDataType } from "@core/reports/utils/fieldTypeUtils";

type Props = {
  group: ActivityEntryFormGroup;
  unsavedCount: number;
};

/**
 * Read-only Summary tab content. Renders API `subgroup_summary` display values only.
 */
const ActivityEntrySubgroupSummary: React.FC<Props> = ({
  group,
  unsavedCount,
}) => {
  const summary = group.subgroup_summary;
  const columns = summary?.columns ?? [];
  const activities = summary?.activities ?? [];

  return (
    <div className="pt-3">
      {unsavedCount > 0 ? (
        <Alert variant="secondary" className="mb-3 py-2 small text-muted">
          {SUMMARY_UNSAVED_BANNER}
        </Alert>
      ) : null}

      {!summary || (columns.length === 0 && activities.length === 0) ? (
        <div className="p-3 text-muted small">{SUMMARY_EMPTY_STATE}</div>
      ) : (
        activities.map((activity) => (
          <div key={activity.activity_id} className="mb-4">
            <h3 className="h6 mb-2">{activity.activity_name}</h3>
            <div className="table-responsive">
              <Table bordered hover size="sm" className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th scope="col">{SUMMARY_KPI_COLUMN_HEADER}</th>
                    {columns.map((col) => (
                      <th
                        scope="col"
                        key={`sg-${col.report_sub_group_id ?? col.sub_group_name}`}
                      >
                        {col.sub_group_name}
                      </th>
                    ))}
                    <th scope="col">{SUMMARY_TOTAL_HEADER}</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.kpis.map((kpi) => {
                    const numeric = isNumericDataType(kpi.data_type_name ?? "");
                    return (
                      <tr key={kpi.kpi_id}>
                        <th scope="row" className="fw-normal">
                          {kpi.kpi_name}
                        </th>
                        {kpi.cells.map((cell, idx) => (
                          <td
                            key={`${kpi.kpi_id}-${cell.report_sub_group_id ?? idx}`}
                            className={numeric ? "text-end" : undefined}
                          >
                            {cell.display_value}
                          </td>
                        ))}
                        <td className={numeric ? "text-end fw-semibold" : "fw-semibold"}>
                          {kpi.total.display_value}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default ActivityEntrySubgroupSummary;
